import { useState } from 'react';
import { ExternalLink, Loader2, Trash2 } from 'lucide-react';
import { saveShowSocialCards } from '@/lib/socialCards';
import { getTemplate } from '@/components/CardEditor/templates';

export interface SocialCardsManagerProps {
  happeningId: string;
  /** The show's saved cards, keyed by template id. */
  cards: Record<string, string>;
  /** Called after a card is removed, with the card map that is left. */
  onChange?: (remaining: Record<string, string>) => void | Promise<void>;
}

/** A saved card's display name: the template's platform label, else its raw key. */
const labelFor = (templateId: string) => getTemplate(templateId)?.platformLabel ?? templateId;

/**
 * The generated cards attached to a show, with a delete for each — the admin-side
 * counterpart to the artist's card panel. Deleting removes the file from storage
 * and the entry from the show's metadata; re-saving a card from either surface
 * replaces it, so this is for taking one down for good.
 */
export function SocialCardsManager({ happeningId, cards, onChange }: SocialCardsManagerProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const entries = Object.entries(cards).filter(([, url]) => Boolean(url));
  if (entries.length === 0) return null;

  const remove = async (templateId: string, url: string) => {
    setDeletingId(templateId);
    setError(null);
    try {
      const remaining = Object.fromEntries(entries.filter(([key]) => key !== templateId));
      // Metadata first, then the file — a failed storage delete leaves an orphan,
      // while a failed metadata write would leave the show pointing at a deleted
      // object. The helper also ref-counts before removing anything.
      await saveShowSocialCards(happeningId, remaining, { [templateId]: url });
      await onChange?.(remaining);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the card');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-2">
      <div>
        <label className="text-sm font-medium">Social cards</label>
        <p className="text-xs text-muted-foreground">
          Generated announcement cards saved to this show. Deleting a show removes its
          cards too.
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        {entries.map(([templateId, url]) => (
          <div key={templateId} className="w-28 space-y-1">
            <a href={url} target="_blank" rel="noreferrer" className="block">
              {/* A plain preview, so it stays on the non-CORS cache entry (see corsMediaUrl). */}
              <img
                src={url}
                alt={labelFor(templateId)}
                className="h-28 w-28 rounded-md border border-input object-cover"
              />
            </a>
            <div className="flex items-center justify-between gap-1">
              <span className="truncate text-xs text-muted-foreground" title={labelFor(templateId)}>
                {labelFor(templateId)}
              </span>
              <div className="flex shrink-0 items-center gap-1">
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-muted-foreground hover:text-foreground"
                  title="Open the full-size card"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
                <button
                  type="button"
                  onClick={() => remove(templateId, url)}
                  disabled={deletingId !== null}
                  className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                  title="Delete this card"
                >
                  {deletingId === templateId ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
